import React, { useEffect, useRef, useState } from "react";
import UserHeader from "../Dashboard/UserHeader";
import { putUserLogin } from "../handler/LoginAndSingupHandler";
import UserLoader from "../Dashboard/UserLoader";

interface LayoutProps {
  children: React.ReactNode;
}

const AdminLayout: React.FC<LayoutProps> = ({ children }) => {
  const hasRun = useRef(false);
  const [loader, setLoader] = useState(true);

  const userLoginOnRefresh = async () => {
    try {
      const response = await putUserLogin({
        growniverseId: "",
        password: "",
      });

      if (response.statusCode === 200) {
        const userData = response.data.loginResponse;

        if (!userData.profile.isAdmin) {
          window.location.href = "/dashboard";
          return;
        }

        localStorage.setItem("appkey", userData.appKey);
        localStorage.setItem(
          "userProfile",
          JSON.stringify(userData.profile)
        );
        localStorage.setItem(
          "account",
          JSON.stringify(userData.account)
        );

        setLoader(false);
      } else {
        localStorage.clear();
        window.location.href = "/";
      }
    } catch (error) {
      console.error("Admin authentication error:", error);

      localStorage.clear();
      window.location.href = "/";
    }
  };

  useEffect(() => {
    if (!hasRun.current) {
      const appKey = localStorage.getItem("appkey");

      if (!appKey) {
        window.location.href = "/";
      } else {
        userLoginOnRefresh();
      }

      hasRun.current = true;
    }
  }, []);

  /*
   * ---------------------------------------------------------
   * STYLES
   * ---------------------------------------------------------
   */

  const styles = {
    page: {
      minHeight: "100vh",
      width: "100%",
      background:
        "linear-gradient(135deg, #f6f9fc 0%, #eef3f8 50%, #f8fafc 100%)",
      color: "#172033",
      position: "relative" as const,
      overflowX: "hidden" as const,
    },

    /*
     * Header wrapper
     *
     * UserHeader itself can contain the complete left panel.
     * This wrapper keeps the entire header fixed.
     */
    headerWrapper: {
      position: "fixed" as const,
      top: 0,
      left: 0,
      width: "100%",
      zIndex: 9999,
      background: "rgba(255,255,255,0.96)",
      backdropFilter: "blur(18px)",
      WebkitBackdropFilter: "blur(18px)",
      borderBottom: "1px solid rgba(15,23,42,0.08)",
      boxShadow: "0 4px 25px rgba(15,23,42,0.06)",
    },

    /*
     * Main content
     *
     * Important:
     * Give the content enough top space so that the fixed
     * header does NOT cover the first section.
     */
    main: {
      minHeight: "100vh",
      width: "100%",
      paddingTop: "82px",
      boxSizing: "border-box" as const,
      position: "relative" as const,
    },

    content: {
      width: "100%",
      minHeight: "calc(100vh - 82px)",
      boxSizing: "border-box" as const,
      position: "relative" as const,
    },

    loaderContainer: {
      position: "fixed" as const,
      inset: 0,
      zIndex: 10000,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background:
        "linear-gradient(135deg, #f7faff 0%, #eef4f8 100%)",
    },
  };

  /*
   * ---------------------------------------------------------
   * RENDER
   * ---------------------------------------------------------
   */

  if (loader) {
    return (
      <>
        <div style={styles.loaderContainer}>
          <UserLoader />
        </div>

        <style>
          {`
            * {
              box-sizing: border-box;
            }

            html,
            body,
            #root {
              margin: 0;
              padding: 0;
              width: 100%;
              min-height: 100%;
            }

            body {
              background: #f6f9fc;
            }

            ::-webkit-scrollbar {
              width: 7px;
              height: 7px;
            }

            ::-webkit-scrollbar-track {
              background: #f1f5f9;
            }

            ::-webkit-scrollbar-thumb {
              background: #cbd5e1;
              border-radius: 10px;
            }

            ::-webkit-scrollbar-thumb:hover {
              background: #94a3b8;
            }
          `}
        </style>
      </>
    );
  }

  return (
    <div style={styles.page}>
      {/* =====================================================
          FIXED HEADER
      ===================================================== */}

      <div style={styles.headerWrapper}>
        <UserHeader />
      </div>

      {/* =====================================================
          PAGE CONTENT
      ===================================================== */}

      <main style={styles.main}>
        <div style={styles.content}>
          {children}
        </div>
      </main>

      {/* =====================================================
          RESPONSIVE / GLOBAL STYLES
      ===================================================== */}

      <style>
        {`
          * {
            box-sizing: border-box;
          }

          html,
          body,
          #root {
            margin: 0;
            padding: 0;
            width: 100%;
            min-height: 100%;
          }

          body {
            background: #f6f9fc;
            overflow-x: hidden;
          }

          /*
           * Desktop
           */
          @media (min-width: 1200px) {
            main {
              padding-top: 82px !important;
            }
          }

          /*
           * Tablet
           */
          @media (max-width: 1199px) {
            main {
              padding-top: 76px !important;
            }
          }

          /*
           * Mobile
           */
          @media (max-width: 768px) {
            main {
              padding-top: 70px !important;
            }
          }

          /*
           * Small mobile
           */
          @media (max-width: 480px) {
            main {
              padding-top: 64px !important;
            }
          }

          /*
           * Prevent horizontal overflow from dashboard
           */
          img,
          video,
          iframe {
            max-width: 100%;
          }

          button,
          input,
          select,
          textarea {
            font-family: inherit;
          }

          /*
           * Smooth scrolling
           */
          html {
            scroll-behavior: smooth;
          }

          /*
           * Scrollbar
           */
          ::-webkit-scrollbar {
            width: 7px;
            height: 7px;
          }

          ::-webkit-scrollbar-track {
            background: #f1f5f9;
          }

          ::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 10px;
          }

          ::-webkit-scrollbar-thumb:hover {
            background: #94a3b8;
          }
        `}
      </style>
    </div>
  );
};

export default AdminLayout;